import { ComputationGraph, OpNode, Shape, TensorEdge } from './types';

export class GraphBuilder {
  private nodes: OpNode[] = [];
  private edges: TensorEdge[] = [];

  private addNode(node: OpNode): void {
    this.nodes.push(node);
  }

  private addEdge(from: string, to: string, shape: Shape, label?: string): void {
    this.edges.push({ from, to, shape, label });
  }

  input(id: string, outputShape: Shape): this {
    this.addNode({ id, type: 'input', label: 'Input', outputShape });
    return this;
  }

  output(id: string, fromNodeId: string): this {
    this.addNode({ id, type: 'output', label: 'Output', outputShape: [] });
    this.addEdge(fromNodeId, id, []);
    return this;
  }

  linear(id: string, fromNodeId: string, weightShape: Shape, hasBias = false): this {
    const [inFeatures, outFeatures] = weightShape;
    const paramCount =
      typeof inFeatures === 'number' && typeof outFeatures === 'number'
        ? inFeatures * outFeatures + (hasBias ? outFeatures : 0)
        : undefined;
    const outputShape: Shape = [weightShape[1]];
    this.addNode({
      id,
      type: 'linear',
      label: 'Linear',
      sublabel: hasBias ? 'with bias' : undefined,
      outputShape,
      params: {
        weight_shape: weightShape as number[],
        param_count: paramCount
      }
    });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  matmul(id: string, fromA: string, fromB: string, outputShape: Shape): this {
    this.addNode({ id, type: 'matmul', label: 'MatMul', outputShape });
    this.addEdge(fromA, id, outputShape);
    this.addEdge(fromB, id, outputShape);
    return this;
  }

  softmax(id: string, fromNodeId: string, dim: number): this {
    this.addNode({ id, type: 'softmax', label: 'Softmax', sublabel: `dim ${dim}`, outputShape: [] });
    this.addEdge(fromNodeId, id, []);
    return this;
  }

  scale(id: string, fromNodeId: string, labelOrFactor: string | number): this {
    const label = typeof labelOrFactor === 'number' ? `× ${labelOrFactor}` : labelOrFactor;
    this.addNode({ id, type: 'scale', label: 'Scale', sublabel: label, outputShape: [] });
    this.addEdge(fromNodeId, id, []);
    return this;
  }

  reshape(id: string, fromNodeId: string, outputShape: Shape): this {
    this.addNode({ id, type: 'reshape', label: 'Reshape', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  transpose(id: string, fromNodeId: string, outputShape: Shape, permLabel?: string): this {
    this.addNode({ id, type: 'transpose', label: 'Transpose', sublabel: permLabel, outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  permute(id: string, fromNodeId: string, outputShape: Shape, permLabel?: string): this {
    this.addNode({ id, type: 'permute', label: 'Permute', sublabel: permLabel, outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  add(id: string, fromA: string, fromB: string): this {
    this.addNode({ id, type: 'add', label: 'Add', outputShape: [] });
    this.addEdge(fromA, id, []);
    this.addEdge(fromB, id, []);
    return this;
  }

  mul(id: string, fromA: string, fromB: string): this {
    this.addNode({ id, type: 'mul', label: 'Mul', outputShape: [] });
    this.addEdge(fromA, id, []);
    this.addEdge(fromB, id, []);
    return this;
  }

  norm(id: string, fromNodeId: string, type: string): this {
    this.addNode({ id, type: 'norm', label: 'Norm', sublabel: type, outputShape: [] });
    this.addEdge(fromNodeId, id, []);
    return this;
  }

  activation(id: string, fromNodeId: string, name: string): this {
    this.addNode({ id, type: 'activation', label: name, outputShape: [] });
    this.addEdge(fromNodeId, id, []);
    return this;
  }

  embedding(id: string, fromNodeId: string, outputShape: Shape): this {
    const [vocabSize, dModel] = outputShape;
    const paramCount =
      typeof vocabSize === 'number' && typeof dModel === 'number'
        ? vocabSize * dModel
        : undefined;
    this.addNode({
      id,
      type: 'embedding',
      label: 'Embedding',
      outputShape,
      params: {
        weight_shape: outputShape as number[],
        param_count: paramCount
      }
    });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  dropout(id: string, fromNodeId: string, rate: number): this {
    this.addNode({ id, type: 'dropout', label: 'Dropout', sublabel: `p=${rate}`, outputShape: [] });
    this.addEdge(fromNodeId, id, []);
    return this;
  }

  mask(id: string, fromNodeId: string, outputShape: Shape = []): this {
    this.addNode({ id, type: 'mask', label: 'Causal Mask', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  crossEntropy(id: string, fromLogits: string, fromTargets: string): this {
    this.addNode({ id, type: 'cross-entropy', label: 'CrossEntropy', outputShape: [] });
    this.addEdge(fromLogits, id, []);
    this.addEdge(fromTargets, id, []);
    return this;
  }

  gate(id: string, fromNodeId: string, outputShape: Shape): this {
    this.addNode({ id, type: 'gate', label: 'Gate', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  router(id: string, fromNodeId: string, outputShape: Shape): this {
    this.addNode({ id, type: 'router', label: 'Router', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  expert(id: string, fromNodeId: string, outputShape: Shape, index: number): this {
    this.addNode({ id, type: 'expert', label: `Expert ${index}`, outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  selectiveScan(id: string, fromNodeId: string, outputShape: Shape): this {
    this.addNode({ id, type: 'selective-scan', label: 'Selective Scan', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  stateSpace(id: string, fromNodeId: string, outputShape: Shape): this {
    this.addNode({ id, type: 'state-space', label: 'State Space', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  concat(id: string, fromIds: string[], outputShape: Shape): this {
    this.addNode({ id, type: 'concat', label: 'Concat', outputShape });
    for (const fromId of fromIds) {
      this.addEdge(fromId, id, outputShape);
    }
    return this;
  }

  cache(id: string, fromNodeId: string, outputShape: Shape): this {
    this.addNode({ id, type: 'cache', label: 'Cache', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  quantize(id: string, fromNodeId: string, outputShape: Shape, bits: number): this {
    this.addNode({ id, type: 'quantize', label: 'Quantize', sublabel: `${bits}-bit`, outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  dequantize(id: string, fromNodeId: string, outputShape: Shape): this {
    this.addNode({ id, type: 'quantize', label: 'Dequantize', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  cast(id: string, fromNodeId: string, outputShape: Shape, dtype: string): this {
    this.addNode({ id, type: 'cast', label: 'Cast', sublabel: dtype, outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  loss(id: string, fromLogits: string, fromTargets: string): this {
    this.addNode({ id, type: 'loss', label: 'Loss', outputShape: [] });
    this.addEdge(fromLogits, id, []);
    this.addEdge(fromTargets, id, []);
    return this;
  }

  metric(id: string, fromNodeId: string, name: string): this {
    this.addNode({ id, type: 'metric', label: 'Metric', sublabel: name, outputShape: [] });
    this.addEdge(fromNodeId, id, []);
    return this;
  }

  allreduce(id: string, fromNodeId: string, outputShape: Shape): this {
    this.addNode({ id, type: 'allreduce', label: 'AllReduce', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  allgather(id: string, fromNodeId: string, outputShape: Shape): this {
    this.addNode({ id, type: 'allgather', label: 'AllGather', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  reduceScatter(id: string, fromNodeId: string, outputShape: Shape): this {
    this.addNode({ id, type: 'reduce-scatter', label: 'ReduceScatter', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  all2all(id: string, fromNodeId: string, outputShape: Shape): this {
    this.addNode({ id, type: 'all2all', label: 'All2All', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  p2p(id: string, fromNodeId: string, outputShape: Shape): this {
    this.addNode({ id, type: 'p2p', label: 'P2P', outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  split(id: string, fromNodeId: string, outputShape: Shape, label?: string): this {
    this.addNode({ id, type: 'split', label: 'Split', sublabel: label, outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  replicate(id: string, fromNodeId: string, outputShape: Shape, count: number): this {
    this.addNode({ id, type: 'replicate', label: 'Replicate', sublabel: `×${count}`, outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  conv(id: string, fromNodeId: string, outputShape: Shape, label?: string): this {
    this.addNode({ id, type: 'conv', label: 'Conv', sublabel: label, outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  pool(id: string, fromNodeId: string, outputShape: Shape, label?: string): this {
    this.addNode({ id, type: 'pool', label: 'Pool', sublabel: label, outputShape });
    this.addEdge(fromNodeId, id, outputShape);
    return this;
  }

  fusion(id: string, fromA: string, fromB: string, outputShape: Shape): this {
    this.addNode({ id, type: 'fusion', label: 'Fusion', outputShape });
    this.addEdge(fromA, id, outputShape);
    this.addEdge(fromB, id, outputShape);
    return this;
  }

  build(): ComputationGraph {
    // Propagate shapes: nodes with empty outputShape inherit from their first predecessor
    const shapeOf = new Map<string, Shape>();
    for (const n of this.nodes) shapeOf.set(n.id, n.outputShape);

    // Build predecessor map
    const preds = new Map<string, string[]>();
    for (const n of this.nodes) preds.set(n.id, []);
    for (const e of this.edges) preds.get(e.to)?.push(e.from);

    // Topological order (nodes are already added in dependency order by callers)
    for (const node of this.nodes) {
      if (node.outputShape.length === 0) {
        const predIds = preds.get(node.id) || [];
        for (const pid of predIds) {
          const ps = shapeOf.get(pid);
          if (ps && ps.length > 0) {
            node.outputShape = ps;
            shapeOf.set(node.id, ps);
            break;
          }
        }
      }
    }

    // Also fix edge shapes that are empty
    for (const edge of this.edges) {
      if (edge.shape.length === 0) {
        const s = shapeOf.get(edge.from);
        if (s && s.length > 0) edge.shape = s;
      }
    }

    return { nodes: this.nodes, edges: this.edges };
  }
}
